export type BookingNiche = {
  slug: string;
  selectorLabel: string;
  pageTitle: string;
  metaDescription: string;
  h1Heading: string;
  heroText: string;
  image: string;
  imageAlt: string;
  painPoints: {
    title: string;
    text: string;
  }[];
  featureHeading: string;
  features: {
    title: string;
    text: string;
  }[];
  faq: {
    q: string;
    a: string;
  }[];
};

export const generalBookingNiche: BookingNiche = {
  slug: "booking",
  selectorLabel: "saloner og klinikker",
  pageTitle: "Bookingsystem til saloner og klinikker | Nalosoft",
  metaDescription:
    "Få et bookingsystem til saloner og klinikker med online tidsbestilling, påmindelser, betaling og en hjemmeside der gør det nemt at vælge tid.",
  h1Heading: "Bookingsystem til saloner og klinikker",
  heroText:
    "Nalosoft bygger bookingflows, hvor kunder kan se ydelser, vælge tid og føle sig trygge før de booker. Systemet kan formes omkring dine behandlinger, medarbejdere og regler.",
  image: "/cases/mkcosmetics/mkcosmetics-booking1.png",
  imageAlt: "Online bookingflow til beauty- og klinikvirksomhed",
  painPoints: [
    {
      title: "Kunder vælger forkert ydelse",
      text:
        "Hvis varighed, pris og forberedelse ikke er tydelig, ender kunden ofte med at ringe eller booke en tid der ikke passer.",
    },
    {
      title: "Kalenderen kræver for meget manuelt arbejde",
      text:
        "Flytninger, afbud, påmindelser og beskeder tager tid, især når flere medarbejdere eller rum skal passe sammen.",
    },
    {
      title: "Booking ligger væk fra dit brand",
      text:
        "Et eksternt bookinglink kan få oplevelsen til at føles løsrevet fra hjemmesiden og gøre nye kunder mere usikre.",
    },
  ],
  featureHeading: "Et bookingsystem der passer til en aftalebaseret hverdag",
  features: [
    {
      title: "Ydelser med varighed og regler",
      text:
        "Behandlinger kan have egne priser, tidsblokke, pauser, beskrivelser og krav før kunden vælger tid.",
    },
    {
      title: "Påmindelser og tydelig bekræftelse",
      text:
        "Kunden får klar information før sin tid, og virksomheden bruger mindre tid på gentagne beskeder.",
    },
    {
      title: "Hjemmeside og booking i samme oplevelse",
      text:
        "Siden forklarer ydelserne, viser tillid og leder naturligt videre til booking uden at sende kunden væk.",
    },
  ],
  faq: [
    {
      q: "Kan systemet tilpasses flere typer ydelser?",
      a:
        "Ja. Første skridt er at gennemgå ydelser, varigheder, medarbejdere, pauser og regler, så bookingflowet passer til den måde virksomheden arbejder på.",
    },
    {
      q: "Kan booking kobles til en eksisterende hjemmeside?",
      a:
        "Ja. Det kan enten kobles på en nuværende side eller bygges sammen med en ny Nalosoft-hjemmeside, hvis den nuværende side ikke forklarer ydelserne godt nok.",
    },
  ],
};

export const bookingNiches: BookingNiche[] = [
  {
    slug: "skonhedsklinik",
    selectorLabel: "skønhedsklinikker",
    pageTitle: "Bookingsystem til skønhedsklinik | Nalosoft",
    metaDescription:
      "Få et bookingsystem til skønhedsklinik med online tidsbestilling, behandlinger, forberedelsesinfo, påmindelser og et trygt flow for nye kunder.",
    h1Heading: "Bookingsystem til skønhedsklinik",
    heroText:
      "En skønhedsklinik skal skabe tillid før kunden vælger tid. Nalosoft samler behandlinger, priser, forberedelsesinfo og booking i et roligt flow, så flere kunder tør tage næste skridt.",
    image: "/cases/mkcosmetics/mkcosmetics-booking2.png",
    imageAlt: "Bookingflow til skønhedsklinik",
    painPoints: [
      {
        title: "Behandlinger kræver forklaring",
        text:
          "Kunden skal forstå forskellen på konsultation, behandling og opfølgning, før de vælger den rigtige tid.",
      },
      {
        title: "Forberedelse bliver ofte manuel",
        text:
          "Beskeder om hud, medicin, efterpleje eller praktiske hensyn kan hurtigt tage tid, hvis de sendes enkeltvis.",
      },
      {
        title: "Tillid afgør bookingen",
        text:
          "Nye kunder vil se priser, proces, faglighed og forventninger, før de lægger en behandling i kalenderen.",
      },
    ],
    featureHeading: "Vigtige funktioner for en skønhedsklinik",
    features: [
      {
        title: "Konsultation og behandling adskilt",
        text:
          "Forskellige bookingtyper kan have egne varigheder, tekster og beskeder, så kunden vælger mere præcist.",
      },
      {
        title: "Forberedelsesinfo før besøget",
        text:
          "Bekræftelser og påmindelser kan indeholde den information, kunden skal kende før sin tid.",
      },
      {
        title: "Plads til journalnære arbejdsgange",
        text:
          "Hvis klinikken har særlige noter, samtykke eller interne trin, kan de afklares tydeligt før udvikling.",
      },
    ],
    faq: [
      {
        q: "Kan systemet håndtere konsultationer og behandlinger forskelligt?",
        a:
          "Ja. De kan sættes op som forskellige bookingtyper med egne varigheder, beskrivelser og beskeder.",
      },
      {
        q: "Kan kunden få information før sin behandling?",
        a:
          "Ja. Relevant forberedelsesinfo kan indgå i bekræftelse eller påmindelse, afhængigt af behovet.",
      },
    ],
  },
  {
    slug: "frisor",
    selectorLabel: "frisører",
    pageTitle: "Bookingsystem til frisør | Nalosoft",
    metaDescription:
      "Få et bookingsystem til frisør med online tidsbestilling, behandlinger, medarbejdere, påmindelser og en lokal hjemmeside der gør det nemt at booke.",
    h1Heading: "Bookingsystem til frisør",
    heroText:
      "Frisørbooking skal kunne rumme klip, farve, kur, styling, medarbejdere og realistiske varigheder. Nalosoft hjælper med et bookingflow, der giver færre afbrydelser og en kalender kunderne selv kan bruge.",
    image: "/cases/mkcosmetics/mkcosmetics-booking1.png",
    imageAlt: "Bookingkalender til frisør",
    painPoints: [
      {
        title: "Behandlinger tager forskellig tid",
        text:
          "Farve, balayage, klip og styling kan ikke presses ind i samme tidsblok uden at resten af dagen bliver påvirket.",
      },
      {
        title: "Telefonen afbryder arbejdet",
        text:
          "Bookinger, flytninger og spørgsmål kommer ofte mens personalet står med en kunde i stolen.",
      },
      {
        title: "Førstegangskunder vil se mere",
        text:
          "Priser, billeder, medarbejdere og behandlinger skal være tydelige, før en ny kunde tør vælge tid.",
      },
    ],
    featureHeading: "Vigtige funktioner for frisører",
    features: [
      {
        title: "Behandlinger med realistisk varighed",
        text:
          "Ydelser kan sættes op med egne tidsblokke, pauser og tillæg, så kalenderen holder bedre.",
      },
      {
        title: "Medarbejdere og specialer",
        text:
          "Kunden kan vælge en bestemt frisør eller finde en ledig tid hos en medarbejder med den rigtige ydelse.",
      },
      {
        title: "Genbooking og påmindelser",
        text:
          "Faste kunder kan lettere booke igen, og klare beskeder kan mindske udeblivelser.",
      },
    ],
    faq: [
      {
        q: "Kan systemet håndtere flere frisører?",
        a:
          "Ja. Medarbejdere, arbejdstider, ydelser og specialer kan indgå i bookinglogikken.",
      },
      {
        q: "Kan kunder vælge behandlinger med forskellig varighed?",
        a:
          "Ja. Hver behandling kan have sin egen varighed, pris, beskrivelse og eventuelle tillæg.",
      },
    ],
  },
  {
    slug: "kosmetolog",
    selectorLabel: "kosmetologer",
    pageTitle: "Bookingsystem til kosmetolog | Nalosoft",
    metaDescription:
      "Få et bookingsystem til kosmetolog med online booking, behandlinger, hudplejeforløb, påmindelser og en side der gør nye kunder trygge.",
    h1Heading: "Bookingsystem til kosmetolog",
    heroText:
      "Kosmetologkunder skal ofte vælge mellem behandlinger, hudplejeforløb og rådgivning. Nalosoft bygger et bookingflow, der forklarer valgene klart og hjælper kunden trygt videre til en tid.",
    image: "/cases/mkcosmetics/mkcosmetics-homepage.png",
    imageAlt: "Booking og hjemmeside til kosmetolog",
    painPoints: [
      {
        title: "Ydelserne ligner hinanden for kunden",
        text:
          "Ansigtsbehandlinger, bryn, vipper og hudpleje kan være svære at vælge imellem uden en enkel forklaring.",
      },
      {
        title: "Rådgivning og behandling blandes sammen",
        text:
          "Nogle kunder har brug for vejledning først, mens andre er klar til at booke en konkret behandling.",
      },
      {
        title: "Gentagne beskeder tager tid",
        text:
          "Spørgsmål om pris, varighed, forberedelse og efterpleje kan besvares bedre i flowet.",
      },
    ],
    featureHeading: "Vigtige funktioner for kosmetologer",
    features: [
      {
        title: "Ydelsesguidning",
        text:
          "Korte beskrivelser kan hjælpe kunden med at vælge behandling ud fra behov, varighed og pris.",
      },
      {
        title: "Forløb og opfølgning",
        text:
          "Booking kan planlægges omkring gentagne besøg, opfølgende tider og anbefalede intervaller.",
      },
      {
        title: "Troværdig præsentation",
        text:
          "Hjemmesiden kan forklare behandlinger, proces og forventninger, så bookingen føles mere sikker.",
      },
    ],
    faq: [
      {
        q: "Kan systemet vise behandlinger med forklaringer?",
        a:
          "Ja. Hver ydelse kan have en kort beskrivelse, pris, varighed og relevant information før booking.",
      },
      {
        q: "Kan kunder booke rådgivning før behandling?",
        a:
          "Ja. Rådgivning kan være en separat bookingtype med egen varighed og tekst.",
      },
    ],
  },
  {
    slug: "negleklinik",
    selectorLabel: "negleklinikker",
    pageTitle: "Bookingsystem til negleklinik | Nalosoft",
    metaDescription:
      "Få et bookingsystem til negleklinik med online booking, behandlinger, tillæg, designnoter, påmindelser og færre beskeder frem og tilbage.",
    h1Heading: "Bookingsystem til negleklinik",
    heroText:
      "Neglebooking kræver mere end ledige tider. Kunder skal vælge mellem nyt sæt, opfyldning, aftagning, nail art og reparationer uden at gætte sig frem.",
    image: "/cases/mkcosmetics/mkcosmetics-booking1.png",
    imageAlt: "Bookingflow til negleklinik",
    painPoints: [
      {
        title: "Kunder vælger forkert behandling",
        text:
          "Nyt sæt, opfyldning og reparation tager forskellig tid. Forkert valg kan presse kalenderen.",
      },
      {
        title: "Tillæg aftales ofte i beskeder",
        text:
          "Design, længde, aftagning og ekstra reparationer bør være tydelige før kunden booker.",
      },
      {
        title: "Instagram bliver supportkanal",
        text:
          "Mange spørgsmål om priser og tider ender i DM, hvor de tager tid og let bliver overset.",
      },
    ],
    featureHeading: "Vigtige funktioner for negleklinikker",
    features: [
      {
        title: "Tillæg og noter",
        text:
          "Kunden kan vælge relevante tilvalg eller skrive korte noter, så klinikken er bedre forberedt.",
      },
      {
        title: "Behandlinger med tydelig varighed",
        text:
          "Nyt sæt, opfyldning, design og reparation kan have egne tider og regler.",
      },
      {
        title: "Visuel tryghed før booking",
        text:
          "Billeder, priser og regler kan ligge tæt på bookingflowet, så nye kunder ved hvad de vælger.",
      },
    ],
    faq: [
      {
        q: "Kan kunder vælge nail art eller andre tillæg?",
        a:
          "Ja. Tillæg kan indgå som valg i flowet, så kunden booker mere præcist.",
      },
      {
        q: "Kan systemet håndtere opfyldning og nyt sæt forskelligt?",
        a:
          "Ja. De kan have egne varigheder, priser, tekster og regler.",
      },
    ],
  },
  {
    slug: "barber",
    selectorLabel: "barbere",
    pageTitle: "Bookingsystem til barber | Nalosoft",
    metaDescription:
      "Få et bookingsystem til barber med online tidsbestilling, walk-ins, klip, skæg, medarbejdere, påmindelser og en hurtig vej til booking.",
    h1Heading: "Bookingsystem til barber",
    heroText:
      "En barbershop har ofte både faste tider, hurtige behandlinger og kunder der kigger forbi. Nalosoft kan forme et bookingflow, der giver overblik uden at gøre hverdagen tung.",
    image: "/services/websites-service.jpg",
    imageAlt: "Mobilvenlig booking til barber",
    painPoints: [
      {
        title: "Walk-ins og bookinger skal balanceres",
        text:
          "Butikken skal kunne tage spontane kunder uden at ødelægge kalenderen for dem, der har booket.",
      },
      {
        title: "Klip og skæg har korte tidsrum",
        text:
          "Små fejl i varighed eller pauser kan hurtigt skabe ventetid gennem dagen.",
      },
      {
        title: "Kunder vil booke hurtigt",
        text:
          "Barberkunder har ofte brug for en enkel mobilvej til klip, skæg eller kombinationsydelser.",
      },
    ],
    featureHeading: "Vigtige funktioner for barbere",
    features: [
      {
        title: "Booking og walk-ins i samme overblik",
        text:
          "Flowet kan tage højde for tider, buffer og hvor meget kalenderen skal holdes åben til spontane kunder.",
      },
      {
        title: "Hurtige ydelser på mobil",
        text:
          "Klip, skæg og kombinationer kan vælges med få trin, så kunden hurtigt finder en tid.",
      },
      {
        title: "Medarbejdere og faste kunder",
        text:
          "Kunden kan vælge sin foretrukne barber eller finde første relevante ledige tid.",
      },
    ],
    faq: [
      {
        q: "Kan systemet fungere sammen med walk-ins?",
        a:
          "Ja. Det afhænger af butikkens rytme, men bookingregler og buffer kan planlægges, så der stadig er plads til spontane kunder.",
      },
      {
        q: "Kan kunder vælge en bestemt barber?",
        a:
          "Ja. Medarbejdervalg kan være en del af flowet, hvis det passer til butikken.",
      },
    ],
  },
];

export const bookingNicheBySlug = bookingNiches.reduce<
  Record<string, BookingNiche>
>((acc, niche) => {
  acc[niche.slug] = niche;
  return acc;
}, {});

export function getBookingNiche(slug: string | undefined) {
  return slug ? bookingNicheBySlug[slug] ?? generalBookingNiche : generalBookingNiche;
}
